import React, { useCallback, useContext, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { SubmitFormContext } from '../../../context';
import Loader from '@wix/da-ds/pkg/Loader';
import { useTranslation } from 'react-i18next';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { MIME_CONFIG, VIDEO_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import FileIcon from '@wix/da-ds/pkg/Icons/24x24/File';
import ProgressBar from '@wix/da-ds/pkg/ProgressBar';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import type { FileUploadOptions } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';
import ImagePreview from '../ImagePreview';
import { validator } from './validation';

import s from './FilePreview.scss';

export interface Props {
  className?: string;
}

const FilePreview: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const submitFormContext = useContext(SubmitFormContext);
  const {
    initResponse,
    params,
    refreshInitData,
    registerValidator,
    unregisterValidator,
  } = submitFormContext ?? {};
  const { isFormBusy, isMainUploading } = useSubmitFormStatus();

  const needsPreview =
    initResponse?.requiresPreview && !initResponse.hasCustomPreview;

  const deviation = initResponse?.deviation;
  const isFilm = getDeviationType(deviation) === DeviationTypes.FILM;
  const isPublished = deviation?.isPublished;
  const showFakeFilmThumb = isFilm && !isPublished;

  // Add validator to disallow submit when preview upload is required
  useEffect(() => {
    needsPreview && registerValidator?.('filePreview', validator);
    return () => {
      needsPreview && unregisterValidator?.('filePreview');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPreview]);

  const { errors } = useFormContext();

  const hasError = !!errors['filePreview'];

  // These are shared between both preview and original uploads
  const onSuccess = useCallback(() => {
    refreshInitData && refreshInitData();
  }, [refreshInitData]);

  const onFailure = useCallback(
    (payload, error) => {
      dispatch(addErrorNotification(error.message));
    },
    [dispatch]
  );

  // Handle file select for preview files
  const handleDrop = useCallback(() => {
    return true;
  }, []);

  // Setup uploads for preview files
  const { input, activeUploads, getDropzoneRootProps, isDragging } =
    useFileUpload({
      id: 'submit-preview-replacement',
      uploadType: UploadType.DEVIATION_REPLACE_PREVIEW,
      uploadParams: {
        deviationId: params?.deviationId,
      },
      allowMultiple: false,
      allowDragAndDrop: true,
      acceptedMimeTypes: MIME_CONFIG.forFilePreview,
      onDrop: handleDrop,
      onSuccess,
      onFailure,
    } as FileUploadOptions);

  // Handle file select for original file
  const handleReplaceOrig = useCallback(() => {
    return true;
  }, []);

  // Setup uploads for original files
  const { input: inputOrig, showFilePicker: showOrigFilePicker } =
    useFileUpload({
      id: 'submit-orig-replacement',
      uploadType: UploadType.DEVIATION_REPLACE_ORIGINAL,
      uploadParams: {
        deviationId: params?.deviationId,
      },
      allowMultiple: false,
      allowDragAndDrop: false,
      acceptedMimeTypes: isFilm ? VIDEO_MIME : MIME_CONFIG.forDeviationSubmit,
      onDrop: handleReplaceOrig,
      onSuccess,
      onFailure,
    } as FileUploadOptions);

  // Shared between both preview and original
  const activeUpload = [...activeUploads].pop();
  const uploadProgress = activeUpload?.progress;
  const allowUpload = !isFormBusy && !isMainUploading;

  return (
    <div className={cn(s['root'], className)}>
      {inputOrig}
      <div className={s['orig-btn-wrapper']}>
        <IconButton
          className={cn(s['edit-btn'], !allowUpload && s['disabled'])}
          size={'small'}
          icon={FileIcon}
          disabled={!allowUpload}
          onClick={allowUpload ? showOrigFilePicker : undefined}
          tooltipOffset={[0, 8]}
          tooltipText={t('deviationSubmit.form.original.replace.tooltip.text')}
          aria-label={t('deviationSubmit.form.original.replace.tooltip.text')}
        />
      </div>
      {allowUpload && !needsPreview ? input : null}
      <div
        className={s['edit-btn-wrapper']}
        {...(allowUpload && !needsPreview ? getDropzoneRootProps() : {})}
      >
        <IconButton
          className={cn(
            s['edit-btn'],
            (!allowUpload || needsPreview) && s['disabled']
          )}
          size={'small'}
          icon={EditIcon}
          disabled={!allowUpload || needsPreview}
          tooltipOffset={[0, 8]}
          tooltipText={
            allowUpload
              ? t('deviationSubmit.form.preview.replace.tooltip.text')
              : undefined
          }
          aria-label={t('deviationSubmit.form.preview.replace.tooltip.text')}
        />
      </div>
      {needsPreview && allowUpload && (
        <>
          <div
            className={cn(
              s['need-preview-root'],
              isDragging && s['drag-active'],
              hasError && s['with-error']
            )}
            {...(allowUpload ? getDropzoneRootProps() : {})}
          >
            {input}
            <Loader
              className={s['llama']}
              size="big"
              state={showFakeFilmThumb ? 'popcorn' : 'static'}
            />
            <span className={s['title']}>
              {t('deviationSubmit.form.preview.file.title')}
            </span>
            <span className={cn(s['text'], hasError && s['has-error'])}>
              {t('deviationSubmit.form.preview.file.text')}
            </span>
            <GhostButton className={s['add-btn']} variant="main" size="large">
              {t('deviationSubmit.form.preview.file.btn')}
            </GhostButton>
          </div>
        </>
      )}
      {isMainUploading && (
        <div className={s['uploading-wrapper']}>
          <div className={s['uploading']}>
            <Loader size="big" state="eating" />
            {uploadProgress && (
              <ProgressBar
                progress={uploadProgress}
                className={s['progress-bar']}
              />
            )}
            <div className={s['progress-title']}>
              {t('uploadZone.progress.title')}
            </div>
          </div>
        </div>
      )}
      {allowUpload && !needsPreview && (
        <ImagePreview
          className={s['preview']}
          deviation={initResponse?.deviation}
        />
      )}
    </div>
  );
};
FilePreview.displayName = 'FilePreview';

export default FilePreview;
